import React, { useEffect, useState, useContext } from "react"
import { Redirect } from "react-router"
import { db, auth, arrayRemove, arrayUnion, funEU } from "../../utils/firebase"
import { ModalContext } from "../../utils/providers/modal"
import { PenIcon, SaveIcon, AlertIcon } from "../../utils/svgs"
import { Sparkles } from "lucide-react"
import firebase from "firebase"
import moment from "moment"
import randomstring from "randomstring"
import "./enquiry.scss"

/**
 * UI components
 */
import Tile from "../../components/structure/tile/tile"
import Title from "../../components/structure/title/title"
import WindowCard from "../../components/ui/window-card/window-card"
import Input from "../../components/ui/inputs/input"
import Textarea from "../../components/ui/inputs/textarea"
import Button from "../../components/ui/button/button"
import Datepicker from "../../components/ui/datepicker/datepicker"
import Select from "../../components/ui/select/select"
import Navigation from "../../components/ui/navigation/navigation"

/**
 * Right-rail cards
 */
import ClientCard from "./_components/client-card"
import LifecycleCard from "./_components/lifecycle-card"
import SourceCard from "./_components/source-card"
import TagsCard from "./_components/tags-card"

/**
 * Tabs
 */
import ActivityFeed from "./_components/activity/feed"
import ChangeLog from "./_components/activity/change-log"
import ClientHistory from "./_components/client-history"
import ParsingResults from "./_components/parsing-results"
import EnquiryDrafts from "./_components/draft-quotes"
import NewQuote from "./_components/new-quote/new-quote"
import SendMessageModal from "./_components/send-quote-email/send-message-modal"
import SendChannelModal from "./_components/send-quote-email/send-channel-modal"
import SendQuoteEmailModal from "./_components/send-quote-email/send-quote-email"
import TeeSheet from "./_components/tee-sheet/tee-sheet"
import TeeSheetJumpNav from "./_components/tee-sheet/jump-nav"
import { prepareTeeSheetForSave } from "./_components/tee-sheet/model"
import { logActivity, statusLabel } from "./_components/activity/log"

// User ID that is allowed to archive enquiries from stats
const ARCHIVE_FROM_STATS_USER_ID = "CGOHCdUwFAOe13OSTY2D9qdO2LX2"

/**
 * Page-level enquiry view. Replaces the previous modal-based view.
 */
function EnquiryPage(props) {
	const [activeTab, setActiveTab] = useState("DETAIL")
	const [editing, setEditing] = useState(false)
	const [saving, setSaving] = useState(false)
	const [confirming, setConfirming] = useState(false)
	const [removing, setRemoving] = useState(false)
	const [archiving, setArchiving] = useState(false)
	const [isArchivedFromStats, setIsArchivedFromStats] = useState(false)
	const [isDeleted, setIsDeleted] = useState(false)
	const [removedOn, setRemovedOn] = useState(null)
	const [removedByName, setRemovedByName] = useState("")
	const [notFound, setNotFound] = useState(false)
	const [status, setStatus] = useState("")
	const [staticStatus, setStaticStatus] = useState("")
	const [clientID, setClientID] = useState("")
	const [agent, setAgent] = useState("")
	const [staticAgent, setStaticAgent] = useState("")
	const [site, setSite] = useState("")
	const [agents, setAgents] = useState({})
	const [client, setClient] = useState({})
	const [created, setCreated] = useState({})
	const [dateFromMillis, setDateFromMillis] = useState(0)
	const [location, setLocation] = useState("")
	const [nights, setNights] = useState("")
	const [rounds, setRounds] = useState("")
	const [groupSize, setGroupSize] = useState("")
	const [quoted, setQuoted] = useState("")
	const [staticQuoted, setStaticQuoted] = useState("")
	const [notes, setNotes] = useState("")
	const [followUpDate, setFollowUpDate] = useState(0)
	const [staticFollowUpDate, setStaticFollowUpDate] = useState(0)
	const [redirect, setRedirect] = useState("")
	const [parameters, setParameters] = useState({})
	const [inboundChannel, setInboundChannel] = useState(null)
	const [teeSheet, setTeeSheet] = useState(null)
	const [savingTeeSheet, setSavingTeeSheet] = useState(false)
	const [reprocessingTeeSheet, setReprocessingTeeSheet] = useState(false)
	const [teeSheetReprocessError, setTeeSheetReprocessError] = useState("")
	// Parsing results saved by the Enquiry Ingest tool. Optional — when
	// absent we hide the "Parsing results" tab entirely. Shape mirrors what
	// `persistWebsiteEnquiryParse` writes: { result, output_text, model,
	// usage, parsed_at, settings: { fields, predictions, ... } }.
	const [aiParsing, setAiParsing] = useState(null)

	// Bumped after a draft quote is saved (in the builder) or deleted (in the
	// Quotes panel) so the panel re-pulls this enquiry's drafts.
	const [draftsReloadToken, setDraftsReloadToken] = useState(0)

	// Quote send flow, lifted here so it can be triggered both from a draft
	// card's Send icon and from the builder's "Update & Send" button (which may
	// be the "New Quote" builder or the edit-a-draft builder). messageQuote ->
	// the personal-message step; channelQuote -> channel picker; emailQuote ->
	// the quote email modal.
	const [messageQuote, setMessageQuote] = useState(null)
	const [channelQuote, setChannelQuote] = useState(null)
	const [emailQuote, setEmailQuote] = useState(null)
	// Validity ({ amount, unit }) chosen in the channel picker, passed to the email modal.
	const [emailValidFor, setEmailValidFor] = useState(null)

	const { enquiryID } = props
	const { showModal } = useContext(ModalContext)

	/**
	 * Load the enquiry document
	 */
	useEffect(() => {
		const hydrateFromEnquiryData = (data, deleted = false) => {
			const {
				status,
				agent,
				client,
				created,
				date_from,
				location,
				nights,
				rounds,
				group_size,
				quoted,
				notes,
				follow_up,
				site,
				tracking,
				inbound_channel,
				archived_from_stats,
				ai_parsing,
				tee_sheet,
			} = data

			setStatus(status)
			setStaticStatus(status)
			setAgent(agent)
			setStaticAgent(agent)
			setSite(site)
			setCreated(created)
			setLocation(location)
			setNights(nights)
			setRounds(rounds)
			setGroupSize(group_size)
			setQuoted(quoted)
			setStaticQuoted(quoted)
			setNotes(notes)
			setClientID(client)
			setParameters(tracking || {})
			setInboundChannel(inbound_channel || null)
			setTeeSheet(tee_sheet || null)
			setIsArchivedFromStats(archived_from_stats === true)
			setIsDeleted(deleted)
			setAiParsing(ai_parsing || null)
			const followUpMillis = moment(follow_up?.seconds, "X").valueOf()
			setFollowUpDate(followUpMillis || 0)
			setStaticFollowUpDate(followUpMillis || 0)
			setDateFromMillis(date_from?.seconds ? moment(date_from.seconds, "X").valueOf() : 0)
		}

		db.doc(`enquiries/${enquiryID}`)
			.get()
			.then((enquiryDoc) => {
				if (enquiryDoc.exists) {
					hydrateFromEnquiryData(enquiryDoc.data(), false)
					return
				}

				// Fall back to the archived copy so deleted enquiries open in
				// the same page with a banner instead of 404'ing.
				return db
					.doc(`enquiries_removed/${enquiryID}`)
					.get()
					.then((removedDoc) => {
						if (!removedDoc.exists) {
							setNotFound(true)
							return
						}
						const data = removedDoc.data()
						hydrateFromEnquiryData(data.enquiry || {}, true)
						setRemovedOn(data.created || null)
						if (data.agent) {
							db.doc(`users/${data.agent}`)
								.get()
								.then((userDoc) => {
									const u = userDoc.data()
									if (u) setRemovedByName(`${u.first_name || ""} ${u.last_name || ""}`.trim())
								})
						}
					})
			})
	}, [enquiryID])

	/**
	 * Pull assignable agents
	 */
	useEffect(() => {
		db.collection("users")
			.orderBy("first_name")
			.where("show_for.assignable", "==", true)
			.get()
			.then((agentDocs) => {
				const agentsArr = []
				agentDocs.forEach((agentDoc) => {
					const { first_name, last_name } = agentDoc.data()
					agentsArr[agentDoc.id] = `${first_name} ${last_name}`
				})
				setAgents(agentsArr)
			})
	}, [])

	/**
	 * Load the client document when ID resolves
	 */
	useEffect(() => {
		clientID &&
			db.doc(`clients/${clientID}`)
				.get()
				.then((clientDoc) => setClient(clientDoc.data() || {}))
	}, [clientID])

	/**
	 * Save edits to the enquiry document. Diffs the meaningful fields
	 * before/after and writes a typed activity entry for each change so
	 * the feed reflects who did what and when.
	 */
	const saveEnquiryDetails = async () => {
		setSaving(true)
		const followUpTimestamp = firebase.firestore.Timestamp.fromMillis(followUpDate)
		const dateFromTimestamp = dateFromMillis ? firebase.firestore.Timestamp.fromMillis(dateFromMillis) : null

		await db.doc(`enquiries/${enquiryID}`).set(
			{
				agent,
				location,
				nights,
				rounds,
				group_size: groupSize,
				quoted,
				notes: notes || "",
				status,
				follow_up: followUpDate ? followUpTimestamp : null,
				date_from: dateFromTimestamp,
			},
			{ merge: true }
		)

		const enquiryPath = `enquiries/${enquiryID}`

		// Status change
		if (status !== staticStatus) {
			const from = statusLabel(staticStatus)
			const to = statusLabel(status)
			await logActivity(
				enquiryPath,
				"STATUS_CHANGED",
				staticStatus ? `Status changed from ${from} to ${to}` : `Status set to ${to}`
			)
			setStaticStatus(status)
		}

		// Agent reassignment — keep legacy LOGS_enquiries write for back-compat
		if (agent !== staticAgent) {
			await db.collection(`LOGS_enquiries`).add({
				type: "REASSIGNED",
				created: firebase.firestore.FieldValue.serverTimestamp(),
				enquiry: enquiryID,
				fromUser: staticAgent,
				toUser: agent,
				byUser: auth.currentUser.uid,
			})

			const fromName = staticAgent ? agents[staticAgent] || "Unknown" : "Unassigned"
			const toName = agent ? agents[agent] || "Unknown" : "Unassigned"
			await logActivity(enquiryPath, "AGENT_REASSIGNED", `Reassigned from ${fromName} to ${toName}`)
			setStaticAgent(agent)
		}

		// Follow-up date set / changed / cleared
		if (followUpDate !== staticFollowUpDate) {
			const fmt = (millis) => (millis ? moment(millis).format("MMM D, YYYY") : null)
			const fromStr = fmt(staticFollowUpDate)
			const toStr = fmt(followUpDate)
			let body
			if (!staticFollowUpDate && followUpDate) body = `Follow up scheduled for ${toStr}`
			else if (staticFollowUpDate && !followUpDate) body = `Follow up cleared (was ${fromStr})`
			else body = `Follow up moved from ${fromStr} to ${toStr}`
			await logActivity(enquiryPath, "FOLLOW_UP_SCHEDULED", body)
			setStaticFollowUpDate(followUpDate)
		}

		// Quote change — only log when there is a meaningful numeric change
		const prevQuote = Number(staticQuoted) || 0
		const nextQuote = Number(quoted) || 0
		if (prevQuote !== nextQuote) {
			const body = prevQuote === 0
				? `Quote set to £${nextQuote.toLocaleString()}`
				: `Quote changed from £${prevQuote.toLocaleString()} to £${nextQuote.toLocaleString()}`
			await logActivity(enquiryPath, "QUOTE_CHANGED", body)
			setStaticQuoted(quoted)
		}

		setEditing(false)
		setSaving(false)
	}

	const saveTeeSheet = async (draft) => {
		setSavingTeeSheet(true)
		setTeeSheetReprocessError("")
		const localUpdatedAt = firebase.firestore.Timestamp.now()
		const writePayload = prepareTeeSheetForSave(
			draft,
			auth.currentUser?.uid,
			firebase.firestore.FieldValue.serverTimestamp()
		)
		const localPayload = prepareTeeSheetForSave(draft, auth.currentUser?.uid, localUpdatedAt)

		try {
			await db.doc(`enquiries/${enquiryID}`).set({ tee_sheet: writePayload }, { merge: true })
			await logActivity(
				`enquiries/${enquiryID}`,
				teeSheet?.updated_at ? "TEE_SHEET_UPDATED" : "TEE_SHEET_CREATED",
				teeSheet?.updated_at ? "Tee Sheet updated" : "Tee Sheet created"
			)
			setTeeSheet(localPayload)
			return localPayload
		} catch (err) {
			setTeeSheetReprocessError(err?.message || "Could not save Tee Sheet.")
			return null
		} finally {
			setSavingTeeSheet(false)
		}
	}

	const reprocessTeeSheet = async () => {
		setReprocessingTeeSheet(true)
		setTeeSheetReprocessError("")
		try {
			const callable = funEU.httpsCallable("reprocessEnquiryTeeSheet")
			const response = await callable({ enquiry_id: enquiryID })
			const data = response.data || {}
			if (data.ai_parsing) setAiParsing(data.ai_parsing)
			await logActivity(
				`enquiries/${enquiryID}`,
				"TEE_SHEET_REPROCESSED",
				`Tee Sheet reprocessed with AI${data.version ? ` as v${data.version}` : ""}`
			)
			setActiveTab("PARSING")
		} catch (err) {
			setTeeSheetReprocessError(err?.message || "Could not reprocess Tee Sheet.")
		} finally {
			setReprocessingTeeSheet(false)
		}
	}

	/**
	 * Confirm before removing
	 */
	const checkToRemoveEnquiry = () => {
		showModal({
			type: "ALERT",
			title: "Are you sure?",
			body: "Once removed, you won't be able to recover the data for this enquiry",
			cancel: { label: "Cancel", action: () => null },
			next: { label: "Yes, i'm sure", action: () => removeEnquiry() },
		})
	}

	/**
	 * Remove the enquiry
	 */
	const removeEnquiry = async () => {
		setRemoving(true)
		if (clientID) {
			await db.doc(`clients/${clientID}`).set(
				{ enquiries: arrayRemove(enquiryID) },
				{ merge: true }
			)
		}
		await db.doc(`enquiries/${enquiryID}`).delete()
		setRemoving(false)
		setRedirect("/enquiries")
	}

	/**
	 * Stats archive toggles
	 */
	const checkToArchiveFromStats = () => {
		showModal({
			type: "ALERT",
			title: "Archive from stats?",
			body: "This will exclude this enquiry from all conversion metrics and statistics. This action can be undone.",
			cancel: { label: "Cancel", action: () => null },
			next: { label: "Yes, archive from stats", action: () => archiveFromStats() },
		})
	}

	const archiveFromStats = async () => {
		setArchiving(true)
		try {
			await db.doc(`enquiries/${enquiryID}`).set({ archived_from_stats: true }, { merge: true })
			setIsArchivedFromStats(true)
		} catch (err) {
			console.error("Error archiving enquiry from stats:", err)
		} finally {
			setArchiving(false)
		}
	}

	const restoreToStats = async () => {
		setArchiving(true)
		try {
			await db.doc(`enquiries/${enquiryID}`).set({ archived_from_stats: false }, { merge: true })
			setIsArchivedFromStats(false)
		} catch (err) {
			console.error("Error restoring enquiry to stats:", err)
		} finally {
			setArchiving(false)
		}
	}

	/**
	 * Generate a unique 7-digit booking reference
	 */
	const createABookingReference = async () => {
		let reference = ""
		while (!reference) {
			const temporaryReference = randomstring.generate({ length: 7, charset: "123456789" })
			await db
				.collection("bookings")
				.where("reference", "==", reference)
				.get()
				.then((bookingDoc) => {
					if (bookingDoc.exists) return false
					reference = temporaryReference
				})
		}
		return reference
	}

	/**
	 * Convert to a booking — preserves existing destructive behaviour
	 */
	const confirmEnquiryAsBooking = async () => {
		setConfirming(true)
		const bookingReference = await createABookingReference()
		const bookingID = await db
			.collection("bookings")
			.add({
				agent,
				client: clientID,
				reference: Number(bookingReference),
				booked: firebase.firestore.FieldValue.serverTimestamp(),
				created: firebase.firestore.FieldValue.serverTimestamp(),
				date_from: firebase.firestore.Timestamp.fromMillis(dateFromMillis),
				enquiry: {
					date_from: firebase.firestore.Timestamp.fromMillis(dateFromMillis),
					location: location || "",
					nights: nights || 0,
					rounds: rounds || 0,
					group_size: groupSize || 0,
					quoted: quoted || 0,
					notes: notes || "",
					created,
					status,
					site,
					inbound_channel: inboundChannel,
				},
				paid_by_client: 0,
				removed: false,
			})
			.then((bookingDoc) => bookingDoc.id)

		await db.collection(`bookings/${bookingID}/logs`).add({
			type: "BOOKING_CREATED",
			message: "Booking was created from an existing enquiry",
			user: auth.currentUser.uid,
			created: firebase.firestore.FieldValue.serverTimestamp(),
		})

		await db.doc(`clients/${clientID}`).set(
			{
				enquiries: arrayRemove(enquiryID),
				bookings: arrayUnion(bookingID),
			},
			{ merge: true }
		)

		await db.collection(`clients/${clientID}/logs`).add({
			type: "BOOKING_CREATED",
			badge: "SUCCESS",
			message: `Enquiry was converted to a booking for ${location}`,
			user: auth.currentUser.uid,
			created: firebase.firestore.FieldValue.serverTimestamp(),
		})

		// Preserve the enquiry's activity log on the new booking so it can be
		// surfaced from the booking's Activity tab. The enquiry doc is about
		// to be deleted, so we copy entries (and their replies) under
		// `bookings/{id}/enquiry_activity` keyed by their original IDs.
		const activitySnap = await db.collection(`enquiries/${enquiryID}/activity`).get()
		await Promise.all(
			activitySnap.docs.map(async (entryDoc) => {
				const targetEntry = db.doc(`bookings/${bookingID}/enquiry_activity/${entryDoc.id}`)
				await targetEntry.set(entryDoc.data())

				const repliesSnap = await db
					.collection(`enquiries/${enquiryID}/activity/${entryDoc.id}/replies`)
					.get()
				await Promise.all(
					repliesSnap.docs.map((replyDoc) =>
						db
							.doc(`bookings/${bookingID}/enquiry_activity/${entryDoc.id}/replies/${replyDoc.id}`)
							.set(replyDoc.data())
					)
				)
			})
		)

		// Re-home this enquiry's sent quotes before the enquiry (and the path the
		// client page walks) disappears. The public projection survives the delete,
		// so stamping the client + booking onto it keeps each quote - and its live
		// /quote/{token} link - on the client's Quotes tab and ties it to the booking
		// it became. Older quotes published before client-stamping land their client
		// here too. Only published quotes (those with a public_token) are affected.
		const quotesSnap = await db.collection(`enquiries/${enquiryID}/quotes`).get()
		await Promise.all(
			quotesSnap.docs.map(async (quoteDoc) => {
				const token = quoteDoc.data()?.public_token
				if (!token) return
				await db.doc(`public_quotes/${token}`).set(
					{ client: clientID, booking_id: bookingID },
					{ merge: true }
				)
			})
		)

		await db.doc(`enquiries/${enquiryID}`).delete()

		setConfirming(false)
		setRedirect(`/booking/${bookingID}`)
	}

	if (redirect) return <Redirect to={redirect} />

	if (notFound) {
		return (
			<Tile fullPage={true}>
				<Title>
					<h1>Enquiry not found</h1>
				</Title>
				<p>This enquiry may have been removed or converted to a booking.</p>
			</Tile>
		)
	}

	const headerName = client.first_name ? `${client.first_name}${client.last_name ? ` ${client.last_name}` : ""}'s enquiry` : "Enquiry"

	return (
		<Tile fullPage={true}>
			<div className="enquiry-page-header">
				<Title>
					<h1>{headerName}</h1>
				</Title>
			</div>

			{isDeleted && (
				<div className="enquiry-banner is-warning is-page-level" role="status">
					<span className="enquiry-banner-icon" aria-hidden="true">
						<AlertIcon />
					</span>
					<div className="enquiry-banner-body">
						<p className="enquiry-banner-title">This enquiry has been deleted</p>
						<p className="enquiry-banner-detail">
							{removedByName && `Removed by ${removedByName}`}
							{removedByName && removedOn?.seconds && " · "}
							{removedOn?.seconds && `Removed on ${moment(removedOn.seconds, "X").format("MMM D, YYYY")}`}
							{!removedByName && !removedOn?.seconds && "This enquiry is read-only and cannot be edited."}
						</p>
					</div>
				</div>
			)}

			<div className="enquiry-page-tabs">
				<Navigation
					links={[
						{ id: "DETAIL", label: "Detail" },
						{ id: "SHEET", label: "Tee sheet" },
						{ id: "QUOTES", label: "Quotes" },
						{ id: "ACTIVITY", label: "Activity" },
						{ id: "HISTORY", label: "Client history" },
						// Only surface the parsing-results tab when the enquiry has
						// actually been parsed by the Enquiry Ingest tool — otherwise
						// it'd just lead to an empty page.
						...(aiParsing ? [{ id: "PARSING", label: "Parsing results" }] : []),
						{ id: "LOGS", label: "Logs" },
					]}
					active={activeTab}
					onClick={(id) => setActiveTab(id)}
				/>
			</div>

			<div className="enquiry-page-grid">
				<div className="enquiry-page-main">
			{activeTab === "DETAIL" && (
				<>
			<WindowCard
				title="Enquiry summary"
				primaryAction={isDeleted ? null : {
					icon: editing ? <SaveIcon /> : <PenIcon />,
					label: editing ? "Save" : "Edit",
					onClick: editing ? () => saveEnquiryDetails() : () => setEditing(true),
					loading: saving,
					loadingText: "Saving...",
				}}>
				<div className="enquiry-form-stack">
					<div className="input-grid cols-3">
						<Select
							showLabel
							value={agent}
							placeholder="Assigned agent:"
							selected={agent}
							readOnly={!editing}
							onSelect={(option) => setAgent(option.option)}
							options={agents}
						/>

						<Select
							showLabel
							value={status}
							placeholder="Lead type:"
							selected={status}
							readOnly={!editing}
							onSelect={(option) => setStatus(option.option)}
							options={{
								HOT: "Hot",
								FREEPHONE: "Freephone",
								BEING_PROCESSED: "Processing",
							}}
						/>

						<Datepicker
							placeholder="Follow up date:"
							value={followUpDate}
							onSelect={(date) => setFollowUpDate(date)}
							readOnly={!editing}
							showLabel
						/>
					</div>

					<div className="input-grid cols-2">
						<Input
							type="text"
							label="Location:"
							value={location}
							onChange={setLocation}
							placeholder="Location:"
							readOnly={!editing}
							showLabel
						/>

						<Datepicker
							placeholder="Check in:"
							value={dateFromMillis}
							onSelect={(date) => setDateFromMillis(date)}
							readOnly={!editing}
							showLabel
						/>
					</div>

					<div className="input-grid cols-4">
						<Input
							type="text"
							label="Nights:"
							value={nights}
							onChange={setNights}
							placeholder="Nights:"
							readOnly={!editing}
							showLabel
						/>

						<Input
							type="text"
							label="Rounds:"
							value={rounds}
							onChange={setRounds}
							placeholder="Rounds:"
							readOnly={!editing}
							showLabel
						/>

						<Input
							type="text"
							label="Group size:"
							value={groupSize}
							onChange={setGroupSize}
							placeholder="Group size:"
							readOnly={!editing}
							showLabel
						/>

						<Input
							type="text"
							label="Quoted:"
							value={quoted}
							onChange={setQuoted}
							placeholder="Quoted:"
							readOnly={!editing}
							showLabel
						/>
					</div>

					<Textarea
						type="text"
						label="Private notes:"
						value={notes}
						onChange={setNotes}
						placeholder="Private notes:"
						readOnly={!editing}
						showLabel={editing}
						rows={10}
					/>
				</div>

				{isArchivedFromStats && (
					<div className="enquiry-banner is-warning" role="status">
						<span className="enquiry-banner-icon" aria-hidden="true">
							<AlertIcon />
						</span>
						<div className="enquiry-banner-body">
							<p className="enquiry-banner-title">Excluded from conversion statistics</p>
							<p className="enquiry-banner-detail">
								This enquiry won't be counted in any reports or stats. You can restore it at any time below.
							</p>
						</div>
					</div>
				)}

				{!isDeleted && (
				<div className="ui-window-actions">
					<Button
						small={true}
						label="Confirm as booking"
						loadingText="Saving as booking..."
						loading={confirming}
						onClick={() => confirmEnquiryAsBooking()}
					/>

					<Button
						small={true}
						className="OUTLINE"
						label="Remove enqiury"
						loadingText="Removing..."
						loading={removing}
						onClick={() => checkToRemoveEnquiry()}
					/>

					{auth.currentUser?.uid === ARCHIVE_FROM_STATS_USER_ID && (
						<>
							{isArchivedFromStats ? (
								<Button
									small={true}
									className="OUTLINE"
									label="Restore to stats"
									loadingText="Restoring..."
									loading={archiving}
									onClick={() => restoreToStats()}
								/>
							) : (
								<Button
									small={true}
									className="OUTLINE"
									label="Archive from stats"
									loadingText="Archiving..."
									loading={archiving}
									onClick={() => checkToArchiveFromStats()}
								/>
							)}
						</>
					)}
				</div>
				)}
			</WindowCard>

			<WindowCard
				title="Generated options"
				className="is-generated-options-disabled">
				<div className="enquiry-coming-soon">
					<span className="enquiry-coming-soon-icon" aria-hidden="true">
						<Sparkles size={16} strokeWidth={1.75} />
					</span>
					<h4 className="enquiry-coming-soon-title">Coming soon</h4>
					<p className="enquiry-coming-soon-body">
						Suggested package options for this enquiry will be generated and listed here.
					</p>
				</div>
			</WindowCard>

				</>
			)}

			{activeTab === "ACTIVITY" && (
				<>
					<Textarea
						type="text"
						label="Client notes:"
						value={notes}
						onChange={() => {}}
						placeholder="Client notes:"
						readOnly={true}
						showLabel
						rows={10}
					/>

					<ActivityFeed basePath={`enquiries/${enquiryID}`} />
				</>
			)}

			{activeTab === "QUOTES" && (
				<>
					<NewQuote
						seed={{
							enquiryID,
							checkInMillis: dateFromMillis,
							nights,
							rounds,
							groupSize,
						}}
						onSaved={() => setDraftsReloadToken((token) => token + 1)}
						onRequestSend={(quote) => setMessageQuote(quote)}
					/>
					<EnquiryDrafts
						enquiryID={enquiryID}
						reloadToken={draftsReloadToken}
						onDraftChanged={() => setDraftsReloadToken((token) => token + 1)}
						onRequestSend={(quote) => setMessageQuote(quote)}
					/>

					{/* Send flow (shared by the card Send icon + the builder's
					    "Update & Send"): add an optional client message, pick a
					    channel, then send the quote email. The message is persisted on
					    the quote and carried forward so the publish projects it. */}
					{messageQuote && (
						<SendMessageModal
							quote={messageQuote}
							enquiryID={enquiryID}
							onClose={() => setMessageQuote(null)}
							onContinue={(message) => {
								setChannelQuote({ ...messageQuote, agent_message: message })
								setMessageQuote(null)
							}}
						/>
					)}

					{channelQuote && (
						<SendChannelModal
							draft={channelQuote}
							onClose={() => setChannelQuote(null)}
							onEmail={(validFor) => {
								setEmailValidFor(validFor)
								setEmailQuote(channelQuote)
								setChannelQuote(null)
							}}
						/>
					)}

					{emailQuote && (
						<SendQuoteEmailModal
							draft={emailQuote}
							enquiryID={enquiryID}
							validFor={emailValidFor}
							onClose={() => setEmailQuote(null)}
							onSent={() => setDraftsReloadToken((token) => token + 1)}
						/>
					)}
				</>
			)}

			{activeTab === "SHEET" && (
				<TeeSheet
					teeSheet={teeSheet}
					client={client}
					onSave={saveTeeSheet}
					saving={savingTeeSheet}
				/>
			)}

			{activeTab === "HISTORY" && (
				<ClientHistory clientID={clientID} currentEnquiryID={enquiryID} />
			)}

			{activeTab === "PARSING" && aiParsing && (
				<ParsingResults aiParsing={aiParsing} />
			)}

			{activeTab === "LOGS" && (
				<ChangeLog basePath={`enquiries/${enquiryID}`} />
			)}
				</div>

				<aside className="enquiry-page-side">
					{activeTab === "SHEET" && <TeeSheetJumpNav teeSheet={teeSheet} />}
					<ClientCard clientID={clientID} currentEnquiryID={enquiryID} />
					<LifecycleCard enquiryID={enquiryID} created={created} followUpDate={followUpDate} />
					{activeTab !== "SHEET" && !isDeleted && <TagsCard enquiryID={enquiryID} />}
					{activeTab !== "SHEET" && <SourceCard inboundChannel={inboundChannel} tracking={parameters} site={site} />}
				</aside>
			</div>
		</Tile>
	)
}

/**
 * Wrapper to pull the route parameter
 */
function EnquiryPageWrap(props) {
	const { enquiryID } = props.match.params
	return <EnquiryPage enquiryID={enquiryID} />
}

export default EnquiryPageWrap
